<template>
    <v-dialog
      v-model="openNewConversationDialog"
      @input="v => v || onCloseDialog()"
      max-width="600px"
      persistent
      class="new-conversation-dialog"
    >
    <v-card>
        <v-card class="pa-2">
          <v-card-title>
            {{$t('chat.newConversation.title')}}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-form
              v-model="valid"
              ref="form"
            >
                <v-select
                  v-model="selector"
                  :items="currentUser.formSelector()"
                  :label="$t('chat.newConversation.conversationType')"
                  data-testid="new-conversation-conversation-type"
                  required
                ></v-select>
                <v-autocomplete
                  v-model="formData.destinations"
                  data-testid="new-conversation-users"
                  v-if="!isASms()"
                  :required="!isASms()"
                  :rules="[v => !!v && !isASms() || $t('chat.newConversation.errors.fieldRequired')]"
                  :items="chatUsersFlat"
                  item-text="name"
                  item-value="username"
                  dense
                  clearable
                  chips
                  small-chips
                  multiple
                  :label="$t('chat.newConversation.users')"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      @click="data.select"
                      @click:close="remove(data.item)"
                    >
                      <StatusAvatar :user="getUser(data.item.exten)" avatar-size="24px" badge-size="8px"/>
                      <span class="ml-3">{{ data.item.name }}</span>
                    </v-chip>
                  </template>
                  <template v-slot:item="data">
                    <template v-if="checkType(data)">
                      <v-list-item-content v-text="data.item"></v-list-item-content>
                    </template>
                    <template v-else>
                      <v-list-item-icon>
                        <StatusAvatar :user="getUser(data.item.exten)" avatar-size="36px" badge-size="12px"/>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title v-html="data.item.name"></v-list-item-title>
                        <v-list-item-subtitle v-html="data.item.username"></v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </template>
                </v-autocomplete>
                <v-autocomplete
                  v-if="isASms()"
                  v-model="fromDid"
                  data-testid="new-conversation-sms-from-did"
                  :items="smsDidsNumber"
                  :label="$t('chat.newConversation.fromDID')"
                  chips
                ></v-autocomplete>
                <PhoneNumberAutoComplete v-if="isASms()" data-testid="new-conversation-sms-destination" :required="selector === 'SMS'" :include-extensions="false"/>
                <v-select
                  v-model="selectorChannelType"
                  v-if="isAChannel()"
                  :items="channelTypes"
                  item-text="title"
                  item-value="key"
                  :label="$t('chat.newConversation.channelType')"
                  required
                ></v-select>
                <v-text-field
                    v-model="formData.channelName"
                    data-testid="new-conversation-channel-name"
                    v-if="isAChannel()"
                    :rules="[v => !!v && !channelNameExist() || $t('chat.newConversation.errors.fieldRequiredAndUniq')]"
                    :required="isAChannel()"
                    :hint="$t('chat.newConversation.errors.fieldRequiredAndUniq')"
                    :label="$t('chat.newConversation.channelName')"
                ></v-text-field>
                <v-dialog
                  v-model="emojiOpen"
                  width="325"
                >
                  <VEmojiPicker @select="emojiSelector"/>
                </v-dialog>
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          class="mr-2"
                          @click="openEmojiDialog()"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon>
                            mdi-emoticon-happy-outline
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{$t('chat.newConversation.pickEmojis')}}</span>
                    </v-tooltip>
                  </div>
                  <v-textarea
                      v-model="formData.message"
                      data-testid="new-conversation-message"
                      name="input-7-1"
                      :label="$t('chat.newConversation.message')"
                      :rules="[v => !!v || $t('chat.newConversation.errors.fieldRequired')]"
                      required
                      auto-grow
                  ></v-textarea>
                </div>
                <v-file-input
                  v-model="formData.files"
                  accept="audio/wav,audio/x-wav,audio/ac3,audio/amr,audio/ogg,image/png,image/jpeg,image/gif,image/bmp,video/mp4,video/webm,video/3gpp,video/H264,video/x-msvideo,text/plain,text/csv,application/pdf,text/richtext,text/calendar,text/directory"
                  show-size
                  counter
                  small-chips
                  multiple
                  :label="$t('chat.newConversation.uploadFiles')"
                ></v-file-input>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
                text
                @click="onCloseDialog()"
            >
            {{$t('chat.newConversation.btn.cancel')}}
            </v-btn>
            <v-btn
                color="primary"
                text
                :loading="sending"
                :disabled="!valid"
                @click="submit()"
            >
            {{$t('chat.newConversation.btn.submit')}}
            </v-btn>
        </v-card-actions>
        </v-card>
    </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PhoneNumberAutoComplete from '@/components/contacts/phone_number_autocomplete'
import { VEmojiPicker } from 'v-emoji-picker'
import StatusAvatar from '@/components/buddies/status_avatar'

export default {
  name: 'chat-newConversationDialog',
  data () {
    return {
      selector: 'Chat',
      channelTypes: [
        { key: 'private', title: this.$t('chat.newConversation.private') },
        { key: 'public', title: this.$t('chat.newConversation.public') }
      ],
      valid: null,
      selectorChannelType: 'private',
      emojiOpen: false,
      containFiles: 'hidden',
      fromDid: null,
      formData: {
        isSms: false,
        fromDid: null,
        channelName: null,
        destinations: [],
        smsDestination: null,
        message: '',
        files: null,
        channelType: null
      }
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'chat/authenticatedRocketChatUser',
      openNewConversationDialog: 'chat/openNewConversationDialog',
      chatUsers: 'chat/users',
      smsDids: 'chat/smsDids',
      channels: 'chat/channels',
      groups: 'chat/groups',
      sending: 'chat/sending',
      smsDestination: 'contacts/phoneNumbers/autocompleteSelected',
      users: 'users/listAll'
    }),
    smsDidsNumber: function () {
      const dids = []
      this.smsDids.forEach(did => {
        dids.push({ text: `${did.name} <${did.formatted_did}>`, value: did.formatted_did })
      })
      return dids
    },
    chatUsersFlat: function () {
      const data = []
      this.chatUsers.forEach(chatUser => {
        data.push({
          name: chatUser.name,
          username: chatUser.chat_username,
          exten: chatUser.exten
        })
      })
      return data
    }
  },
  watch: {
    selector () {
      if (this.isASms()) {
        if (this.smsDidsNumber.length === 1) {
          this.fromDid = this.smsDidsNumber[0].value
        }
      }
    },
    openNewConversationDialog () {
      this.resetForm()
    },
    'formData.channelName' (val) {
      this.formData.channelName = val?.replace(/[^A-Z0-9]+/ig, '-')
    }
  },
  components: {
    VEmojiPicker,
    PhoneNumberAutoComplete,
    StatusAvatar
  },
  methods: {
    ...mapActions({
      openDialog: 'chat/openDialog',
      newConversation: 'chat/newConversation',
      getChannels: 'chat/getChannels',
      changeSendingState: 'chat/changeSendingState',
      setSmsDestination: 'contacts/phoneNumbers/setAutocompleteSelected'
    }),

    isAChannel () {
      return this.selector === 'Chat' && this.formData.destinations?.length > 1
    },

    isASms () {
      return this.selector === 'SMS'
    },

    openEmojiDialog () {
      this.emojiOpen = !this.emojiOpen
    },

    emojiSelector (emoji) {
      if (this.formData.message === null) {
        this.formData.message = ''
      }
      this.formData.message = this.formData.message + emoji.data
      this.openEmojiDialog()
    },

    channelNameExist () {
      if (this.isAChannel() && this.formData.channelName?.length > 0) {
        if (this.channels.length > 0) {
          const realmid = this.channels[0].name.split('_')[0]
          return this.channels.some((channel) => channel?.name === `${realmid}_${this.formData.channelName}`) ||
            this.groups.some((group) => group?.name === `${realmid}_${this.formData.channelName}`)
        }
      }
      return false
    },

    resetForm () {
      this.formData = {
        isSms: false,
        fromDid: null,
        channelName: null,
        destinations: [],
        smsDestination: null,
        message: null,
        files: null,
        channelType: null
      }
      this.fromDid = null
      this.selector = 'Chat'
      this.selectorChannelType = 'private'
      this.setSmsDestination(null)
    },

    checkType (data) {
      return typeof data.item !== 'object'
    },

    async submit () {
      await this.changeSendingState()
      if (this.valid) {
        this.formData.isSms = this.isASms()
        if (this.isASms()) {
          this.formData.smsDestination = this.getSmsDestination().replace(/\D/g, '')
          this.formData.fromDid = this.fromDid.replace(/\D/g, '')
        } else {
          this.formData.channelType = this.selectorChannelType
        }
        this.newConversation(this.formData)
      }
    },

    getSmsDestination () {
      if (this.smsDestination && typeof (this.smsDestination) === 'object') {
        return this.smsDestination.phoneNumber
      } else if (this.smsDestination && typeof (this.smsDestination) === 'string') {
        return this.smsDestination
      }
    },

    getUser (exten) {
      return this.users.find((user) => user.exten === exten)
    },

    onCloseDialog () {
      this.openDialog()
      this.resetForm()
    },

    remove (item) {
      const index = this.formData.destinations.indexOf(item.username)
      if (index >= 0) this.formData.destinations.splice(index, 1)
    }
  }
}
</script>

<style scoped>
</style>
